<template>
  <section class="overview" :class="{ 'graph-only': graphOnly }">
    <section v-if="!graphOnly" class="left">
      <header v-if="date || title">
        <div class="date">
          {{ $filters.date(date) }}
        </div>

        <h4 class="secondary-font">{{ title }}</h4>
      </header>

      <div class="stats">
        <stat-item
          v-for="(stat, i) in stats"
          :key="i"
          :label="stat.label"
          :value="stat.value"
          :success="stat.positive"
          :error="stat.negative"
        />
      </div>
    </section>

    <graph
      v-bind="$attrs"
      v-on="$listeners"
      :zoom="zoom"
      :scale="scale"
      :labels="labels"
      :datasets="datasets"
    />
  </section>
</template>

<script>
import moment from 'moment';

import Graph from './Graph';
import StatItem from './StatItem';

const GRAPH_COLORS = ['#34be8b', '#3398db'];

export default {
  components: {
    Graph,
    StatItem,
  },

  props: {
    date: [Object, Date],
    title: String,
    stats: Array,
    graphValues: Array,
    graphOnly: Boolean,
    zoom: {
      type: [Number, Object],
      default: null,
    },
    scale: {
      type: String,
      default: 'linear',
    },
  },

  data() {
    return {
      labels: [],
      datasets: [],
    };
  },

  created() {
    this.setupData();
  },

  watch: {
    graphValues: {
      handler() {
        this.setupData();
      },
      deep: true,
    },

    zoom() {
      this.setupData();
    },
  },

  methods: {
    setupData() {
      const labelMap = {};
      const datasets = [];

      this.graphValues.forEach((graphValue) => {
        if (graphValue.data) {
          Object.keys(graphValue.data).forEach((d) => {
            labelMap[d] = true;
          });
        }
      });

      let labels = Object.keys(labelMap);

      if (this.zoom) {
        const startDate = this.zoom.start ? moment(this.zoom.start) : moment().subtract(this.zoom, 'days');
        const endDate = this.zoom.end ? moment(this.zoom.end) : moment();

        labels = labels.filter((d) => {
          const date = moment(new Date(d));

          return date > startDate && date < endDate;
        });
      }

      labels.sort((a, b) => moment(new Date(a)) - moment(new Date(b)));

      if (this.zoom === null || (typeof this.zoom === 'number' && this.zoom > 92 && labels.length > 100)) {
        const dayGap = Math.ceil(labels.length / 100);

        let min = 0;
        let max = 0;

        if (this.graphValues && this.graphValues.length > 0) {
          for (let i = 0; i < labels.length; i += 1) {
            if (this.graphValues[0].data[labels[min]] > this.graphValues[0].data[labels[i]]) {
              min = i;
            }

            if (this.graphValues[0].data[labels[max]] < this.graphValues[0].data[labels[i]]) {
              max = i;
            }
          }
        }

        labels = labels.filter((_, i) => {
          return i % dayGap === 0
            || i === labels.length - 1
            || i === 0
            || i === min
            || i === max;
        });
      }

      this.graphValues.forEach((graphValue, i) => {
        const values = [];
        const pointBorderColor = [];

        labels.forEach((d) => {
          values.push(graphValue.data && graphValue.data[d] ? graphValue.data[d].toFixed(2) : null);
          pointBorderColor.push(graphValue.pointColors && graphValue.pointColors[d] ? graphValue.pointColors[d] : GRAPH_COLORS[i]);
        });

        const dataset = {
          label: graphValue.label,
          borderColor: GRAPH_COLORS[i],
          pointRadius: 2,
          borderWidth: 3,
          spanGaps: true,
          fill: false,
          data: values,
          pointBorderColor,
          useBorderColorForPointColor: true,
        };

        datasets.push(dataset);
      });

      this.labels = labels;
      this.datasets = datasets;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../scss/variables.scss';

.overview:not(.graph-only) {
  display: grid;
  grid-gap: $gp;
  grid-template-columns: minmax(220px, 1fr) 3fr;

  @media($medium-down) {
    display: block;
  }
}

.date {
  white-space: nowrap;
  font-size: 16px;

  @media($medium-down) {
    font-size: 16px;
  }
}

.stats {
  display: grid;
  grid-auto-flow: row;
  grid-template-rows: auto;
  grid-gap: $gp * .3;
  grid-template-columns: repeat(2, 1fr);

  @media($medium-down) {
    grid-auto-flow: row;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: auto;
    margin-bottom: $gp;
  }

  @media($small-down) {
    grid-auto-flow: row;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: auto;
  }
}
</style>
